import type { ChatAttachedFile, ChatAttachment } from '@/services/api/domains/chatbot.api'
import type { ImportFileType } from '@/pages/library/components/import-file'

/**
 * One file in the composer, from the moment it is picked until the message goes:
 * `preparing` (a picture being resized, a PDF being read), `queued`/`uploading` (a data file on its way
 * to the Library), `ready`, or `refused`/`failed` with the reason shown on the chip.
 */
export interface AttachmentChip {
  id: string
  name: string
  kind: 'image' | 'document' | 'data' | 'other'
  status: 'preparing' | 'queued' | 'uploading' | 'ready' | 'refused' | 'failed'
  /** 0–100, while `uploading`. */
  progress?: number
  reason?: string
  /** A picture's data URL, for the thumbnail. */
  previewUrl?: string
  dataFileType?: ImportFileType
  attachment?: ChatAttachment
  attachedFile?: ChatAttachedFile
}

/** What a turn carries from the chips that are ready. */
export function payloadFromChips(chips: readonly AttachmentChip[]): {
  attachments: ChatAttachment[]
  attachedFiles: ChatAttachedFile[]
} {
  const attachments: ChatAttachment[] = []
  const attachedFiles: ChatAttachedFile[] = []

  for (const chip of chips) {
    if (chip.status !== 'ready') continue
    if (chip.attachment) attachments.push(chip.attachment)
    if (chip.attachedFile) attachedFiles.push(chip.attachedFile)
  }

  return { attachments, attachedFiles }
}

/**
 * Why the message cannot go yet, or null. A chip still being prepared or uploaded holds the send (the
 * model would get a message about a file it cannot see); a failed or refused chip holds it until it is
 * removed, so nobody sends believing a file went with it.
 */
export function sendBlockReason(chips: readonly AttachmentChip[]): string | null {
  if (chips.some(c => c.status === 'preparing' || c.status === 'queued' || c.status === 'uploading')) {
    return 'Waiting for attachments to finish…'
  }

  const failed = chips.find(c => c.status === 'failed')
  if (failed) return `${failed.name}: ${failed.reason ?? 'upload failed'} — remove it to send`

  if (chips.some(c => c.status === 'refused')) return 'Remove the files that cannot be attached to send'

  return null
}
